import type {SelectHTMLAttributes} from "react";

interface SelectOption {
  value: string;
  label: string;
}

interface BaseSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "children"> {
  label?: string;
  error?: string;
  options: SelectOption[];
  placeholder?: string;
}

export const BaseSelect = ({label, error, options, placeholder, id, className = "", ...rest}: BaseSelectProps) => {
  const selectId = id ?? label?.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={selectId}
          className="text-sm font-medium text-(--color-text)"
        >
          {label}
        </label>
      )}

      <select
        id={selectId}
        className={[
          "w-full bg-white border rounded-md py-2 pl-3 pr-8 text-sm text-(--color-text)",
          "outline-none transition-[border-color,box-shadow] duration-(--transition-fast)",
          "focus:border-(--color-accent) focus:ring-2 focus:ring-(--color-accent) focus:ring-opacity-20",
          "appearance-none cursor-pointer",
          error ? "border-(--color-danger)" : "border-(--color-border)",
          rest.disabled ? "bg-(--color-surface-2) cursor-not-allowed text-(--color-text-secondary)" : "",
          className,
        ].join(" ")}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "right 0.75rem center",
        }}
        {...rest}
      >
        {placeholder && (
          <option
            value=""
            disabled
          >
            {placeholder}
          </option>
        )}
        {options.map(opt => (
          <option
            key={opt.value}
            value={opt.value}
          >
            {opt.label}
          </option>
        ))}
      </select>

      {error && <p className="text-xs text-(--color-danger)">{error}</p>}
    </div>
  );
};
